import { createAuthInterceptor, createClient } from '@lastbrand/sdk-http'
import { CANARY_GROUP_ID_HEADER } from '@src/constants/api.headers.constants'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { getB2BHeaders } from '@src/utils/b2b/b2b.utils'
import { getCanaryGroupId } from '@src/utils/canary-group-id'
import { getHeadersWithContentVersion, getLocalisationHeaders } from '@src/utils/general.utils'

import type { AuthInterceptorConfig, CreateClientOptions } from '@lastbrand/sdk-http'

type StorefrontClientOptions = CreateClientOptions & {
  useBearerPrefix?: AuthInterceptorConfig['useBearerPrefix']
  authInterceptor?:
    | boolean
    | {
        tokenSource?: AuthInterceptorConfig['tokenSource']
        useBearerPrefix?: AuthInterceptorConfig['useBearerPrefix']
        enabled?: boolean
      }
  // When true, the B2B store-program header (x-store-program-id) is omitted from
  // default headers — used to fetch D2C responses from a B2B self-serve surface.
  excludeB2BHeaders?: boolean
}

async function getDefaultAuthToken(): Promise<string | undefined> {
  const session = (await AuthUtil.getUserSession()) as { idToken?: { jwtToken?: string } } | null | undefined
  return session?.idToken?.jwtToken
}

function getStorefrontDefaultHeaders(baseURL: string, excludeB2BHeaders = false): Record<string, string> {
  const contentVersionHeaders = (getHeadersWithContentVersion(undefined, baseURL) ?? {}) as Record<string, string>
  const b2bHeaders = excludeB2BHeaders ? {} : getB2BHeaders(baseURL)
  const localisationHeaders = getLocalisationHeaders(baseURL)
  const canaryGroupId = getCanaryGroupId()
  return {
    ...contentVersionHeaders,
    ...localisationHeaders,
    ...b2bHeaders,
    ...(canaryGroupId && { [CANARY_GROUP_ID_HEADER]: canaryGroupId }),
  }
}

export function createStorefrontSdkClient(baseURL: string, options?: StorefrontClientOptions) {
  const storefrontOptions = options
  const authOption = storefrontOptions?.authInterceptor
  const authConfig = authOption != null && typeof authOption === 'object' ? authOption : undefined
  const authEnabled = authOption === true || (authConfig != null && authConfig.enabled !== false)
  const {
    authInterceptor: _authInterceptor,
    useBearerPrefix: legacyUseBearerPrefix,
    interceptors: customInterceptors,
    excludeB2BHeaders,
    ...restOptions
  } = storefrontOptions ?? {}
  const useBearerPrefix = authConfig?.useBearerPrefix ?? legacyUseBearerPrefix ?? true

  const interceptors = [
    ...(authEnabled
      ? [
          createAuthInterceptor({
            tokenSource: authConfig?.tokenSource ?? getDefaultAuthToken,
            useBearerPrefix,
          }),
        ]
      : []),
    ...(customInterceptors ?? []),
  ]

  return createClient(baseURL, {
    ...restOptions,
    getDefaultHeaders: () => getStorefrontDefaultHeaders(baseURL, excludeB2BHeaders),
    interceptors,
  })
}
